import agentService from '../../../_services/agent.service';

/**
 * Stand-ins for the endpoints the Bailey tab is waiting on.
 *
 * Real first, always: each helper uses the server's answer when it has one,
 * so the stand-in switches itself off the day the endpoint ships. The made-up
 * parts only run where the assistant itself is visible (local, dev, staging),
 * and anything invented is flagged `example` so the screen can say so.
 * The contract for each lives in docs/BAILEY_TAB_BACKEND_HANDOVER.md.
 */
// Its own list, not AGENT_VISIBLE: Bailey went live on beta and prod in
// October 2026 and took these with it, so real salons saw "23 regulars are
// overdue" with nobody behind it.
const DEMO_ENVS = ['local', 'dev', 'stag'];
export const DEMO_ON = DEMO_ENVS.indexOf(String(process.env.REACT_APP_ENV || '').toLowerCase()) !== -1;

const payload = (res) => (res && res.data && res.data.responseData) || null;

/**
 * "I noticed": up to three suggestions for this salon.
 *
 * Server ideas win. Without them, two kinds of stand-in: ones read from data
 * the page already has (a draft left switched off, no loyalty card), which are
 * true and shown plainly, and example insights on staging only, which are
 * marked as examples because they are not about this salon.
 */
// Asked once per visit: an endpoint that isn't there yet stays missing until
// the page is reloaded, rather than filling the console on every change.
let ideasMissing = false;

export async function loadIdeas({ drafts = [], loyaltyScheme = null, tools = [] } = {}) {
    if (!ideasMissing) {
        try {
            const list = payload(await agentService.ideas());
            if (Array.isArray(list)) return list.slice(0, 3).map((i) => ({ ...i, source: 'server' }));
        } catch (e) { ideasMissing = true; }
    }

    const has = new Set(tools);
    const out = [];
    const off = drafts.find((d) => d.kind === 'automation' && /switched off/i.test(d.detail || ''));
    if (off) {
        out.push({
            id: `draft-${off.id}`,
            icon: '💌',
            title: `“${off.title}” is ready but switched off.`,
            why: 'Check it over, then switch it on when you’re happy.',
            cta: 'Take a look',
            draft: off,
            source: 'derived'
        });
    }
    if (!loyaltyScheme && has.has('propose_loyalty_scheme')) {
        out.push({
            id: 'no-loyalty',
            icon: '⭐',
            title: 'You don’t have a loyalty card yet.',
            why: 'I can suggest one that suits your prices and how often clients come.',
            cta: 'Suggest one',
            prompt: 'Help me start a loyalty card',
            source: 'derived'
        });
    }
    if (DEMO_ON) {
        if (has.has('diary_week') && has.has('draft_campaign')) {
            out.push({
                id: 'example-gap',
                icon: '📅',
                title: 'Thursday afternoon has 3 empty hours.',
                why: '12 clients usually book around then and haven’t been in for six weeks.',
                cta: 'Help me fill it',
                prompt: 'Help me fill Thursday afternoon',
                source: 'example'
            });
        }
        if (has.has('marketable_audience') && has.has('draft_campaign')) {
            out.push({
                id: 'example-lapsed',
                icon: '👋',
                title: '23 regulars are overdue a visit.',
                why: 'They used to come in every six weeks or so.',
                cta: 'Win them back',
                prompt: 'Draft a win-back text for my lapsed clients',
                source: 'example'
            });
        }
    }
    return out.slice(0, 3);
}

/**
 * Next steps under an answer. The server's list wins (followUps on the done
 * event). On staging, a stand-in picks sensible ones from the cards the answer
 * carried, so the pattern can be seen before the server sends its own.
 */
export function followUpsFor(message) {
    if (!message || message.streaming) return [];
    if (Array.isArray(message.followUps)) return message.followUps.slice(0, 3);
    if (!DEMO_ON || !message.text) return [];
    // Bailey ended on an offer ("Want me to draft it?"): answer it in a tap.
    const para = String(message.text).trim().split(/\n\s*\n/).pop() || '';
    // No lookbehind: older iOS Safari can't parse it.
    const offer = (para.match(/[^.!?]+[.!?]+/g) || []).some((x) => /\?$/.test(x.trim())
        && /^(want me to|shall i|should i|do you want me to|would you like me to|happy for me to)\b/i.test(x.trim()));
    if (offer) {
        return ['Yes, please', 'Not right now'];
    }
    const types = new Set((message.cards || []).map((c) => c.type));
    if (types.has('approval') || types.has('draft')) return ['Who’s on the list?', 'Make it shorter', 'Send it next week instead'];
    if (types.has('metrics')) return ['Break it down by stylist', 'How does that compare with last year?'];
    if (types.has('booking') || types.has('event') || types.has('leave')) return ['Book another one', 'What else is free that day?'];
    if (types.has('setup')) return ['Help me fix the first one'];
    if (types.has('diary')) return ['Help me fill the gaps'];
    return ['What should I do next?'];
}
